import { DateTime } from 'luxon';
import algoliaIndexes from '../data';

export const getUrlAndParams = () => {
  if (typeof window === 'undefined') {
    const fallbackUrl = new URL('https://www.klaviyo.com'); 
    return { url: fallbackUrl, params: new URLSearchParams(fallbackUrl.search) };
  }

  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search);
  return { url, params };
};

/**
 * Extracts and formats the 'select' query parameter from a URLSearchParams object.
 * 
 * @param params - A URLSearchParams instance containing URL query parameters.
 * @param format - Specifies the desired return format: 'string' for raw string or 'array' for a split array.
 * @returns The value of the 'select' parameter, either as a string or an array of strings.
 */
export const getSelectParamValues = (params: URLSearchParams, format: 'string' | 'array') => {
  const selectParamValue = decodeURIComponent(params.get('select') ?? '');
  if (format === 'array') {
    return selectParamValue.split('|').filter(Boolean);
  }
  return selectParamValue;
};

const setUrlSearch = (url: URL, params?: URLSearchParams) => {
  if (typeof window === 'undefined') {
    return;
  }

  url.search = params ? params.toString() : '';
  window.history.pushState({}, '', url.toString());
};

/**
 * Clears all query parameters from the current URL.
 * This resets the URL to its base path without reloading the page.
 */
export const clearAllQueryParams = () => {
  const { url } = getUrlAndParams();
  setUrlSearch(url);
};

/**
 * Updates the 'select' query parameter in the URL based on the provided value.
 * 
 * - If `shouldReplace` is `true`, replaces the existing value(s) entirely.
 * - If `shouldReplace` is `false` or undefined, toggles the given value(s) in/out.
 * - If all values are removed, the 'select' param is deleted from the URL.
 * 
 * @param value - A string or array of strings to be added/toggled in the 'select' param.
 * @param shouldReplace - Whether to fully replace the current values (default: false).
 */
export const updateSelectQueryParam = (
  value: string | string[],
  shouldReplace?: boolean,
) => {
  const { params, url } = getUrlAndParams();
  const selectParamValues = getSelectParamValues(params, 'array');

  const clearSelectQueryParam = () => {
    params.delete('select');
    setUrlSearch(url, params);
  };

  const valueArray = Array.isArray(value) ? value : [value];
  let newSelectParamValue = '';

  if (shouldReplace) {
    newSelectParamValue = valueArray.join('|');
  } else {
    const valuesSet = new Set([...selectParamValues]);
    valueArray.forEach((val) => {
      if (valuesSet.has(val)) {
        valuesSet.delete(val);
      } else {
        valuesSet.add(val);
      }
    });
    newSelectParamValue = Array.from(valuesSet).join('|');
  }
  
  if (!newSelectParamValue) {
    clearSelectQueryParam();
    return;
  }
  
  params.set('select', newSelectParamValue);
  setUrlSearch(url, params);
};

/**
 * Updates the 'query' parameter in the URL.
 * 
 * - If `value` is provided, it is set as the 'query' parameter.
 * - If `value` is empty, the 'query' parameter is removed from the URL.
 * 
 * @param value - The string value to set for the 'query' parameter.
 */
export const updateSearchQueryParam = (value: string) => {    
  const { params, url } = getUrlAndParams();

  if (value) {
    params.set('query', value);
  } else {
    params.delete('query');
  }
  setUrlSearch(url, params);
};

/**
 * Updates the 'resource' query parameter in the URL.
 *
 * - If `value` is provided, sets it as the 'resource' parameter.
 * - If `value` is falsy, removes the 'resource' parameter.
 * - If `shouldClearSelect` is true, also removes the 'select' parameter.
 *
 * @param value - The value to set for the 'resource' parameter.
 * @param shouldClearSelect - Whether to clear the 'select' parameter when updating the resource.
 */
export const updateResourceQueryParam = (value: string, shouldClearSelect?: boolean) => {
  const { params, url } = getUrlAndParams();

  if (shouldClearSelect) {
    if (params.has('select')) {
      params.delete('select');
    }
  }

  params.set('resource', value);
  setUrlSearch(url, params);
};

/**
 * Retrieves the 'resource' query parameter value that matches
 * one of the known Algolia indexes.
 * =
 * @returns A matching string from the index values, or undefined if no match is found.
 */
export const getResourceFromParams = () => {
  const { params } = getUrlAndParams();
  const resourceParam = params.get('resource');

  const ALL_INDEXES = '';

  const indexes = Object.values(algoliaIndexes as Record<string, string>);
  return [ALL_INDEXES, ...indexes].find((index) => {
    return index === resourceParam || index === resourceParam?.toLowerCase();
  });
};

export const getRegion = (shouldDefault?: boolean): string => {
  /*
   * Window object is only defined in the browser:
   * User's timezone relies on the browser.
  */
  if (typeof window === 'undefined') {
    return '';
  }

  const acronymToRegionsMap = {
    APAC: ['Asia', 'Australia'],
    EMEA: ['Europe', 'Africa'],
    NAM: ['America'],
  };

  // Get the zoneName from DateTime (ex. 'America/New_York')
  const zoneName = DateTime.local().zoneName;

  if (!zoneName && !shouldDefault) return '';

  for (const [acronym, regions] of Object.entries(acronymToRegionsMap)) {
    if (regions.some(region => zoneName.includes(region))) {
      return acronym;
    }
  }

  if (shouldDefault) {
    return 'NAM';
  }

  return '';
};

export const formatFallbackItemLabel = (label: string): string => {
  if (!label) return '';
  
  const words = label
    .split(/[_ ]+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());

  return words.join(' ');
};

export const shouldExcludePartnerContent = (isPartner: boolean) => {
  return !isPartner
    || process.env.EXCLUDE_PARTNER_CONTENT === 'true'
    // @ts-ignore: ImportMeta is not defined in a cross-site way
    || import.meta.env.PUBLIC_EXCLUDE_PARTNER_CONTENT === 'true';
};
